import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import {
  useLlmGenerationStream,
  type LlmGenerationStreamReducerArgs,
} from "@/llm-generation/client-runtime"

import {
  DASHBOARD_DRILLDOWN_INSIGHTS_STREAM_TASK_ID,
  dashboardDrilldownInsightsOutputSchema,
  dashboardDrilldownInsightsStatusSchema,
  type DashboardDrilldownInsightRow,
  type DashboardDrilldownInsightsInput,
  type DashboardDrilldownInsightsOutput,
  type DashboardDrilldownInsightsStatus,
} from "../types"

const SUBMIT_INPUT = "Generate dashboard insights"

interface DashboardInsightsStreamMessage {
  id: string
  role: "user" | "assistant" | "system"
  content: string
  timestamp: Date
  isStreaming?: boolean
}

interface DashboardInsightsStreamCustomState {
  output: DashboardDrilldownInsightsOutput | null
  statusSteps: DashboardDrilldownInsightsStatus[]
}

type DashboardDrilldownInsightsStatusState =
  | "idle"
  | "loading"
  | "success"
  | "error"

interface UseDashboardDrilldownInsightsInput {
  chart: DashboardDrilldownInsightsInput["chart"] | null
  rows: DashboardDrilldownInsightRow[]
  enabled: boolean
  autoRun?: boolean
}

interface UseDashboardDrilldownInsightsResult {
  output: DashboardDrilldownInsightsOutput | null
  status: DashboardDrilldownInsightsStatusState
  error: string | null
  isLoading: boolean
  isStale: boolean
  streamedText: string
  statusSteps: DashboardDrilldownInsightsStatus[]
  regenerate: () => void
}

const INITIAL_CUSTOM_STATE: DashboardInsightsStreamCustomState = {
  output: null,
  statusSteps: [],
}

interface CreateMessageInput {
  role: DashboardInsightsStreamMessage["role"]
  content: string
  isStreaming?: boolean
  id?: string
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message
  }
  if (typeof error === "string") {
    return error
  }
  return "Unable to generate insights."
}

function buildRequestKey(input: {
  chart: DashboardDrilldownInsightsInput["chart"]
  rows: DashboardDrilldownInsightRow[]
}): string {
  return JSON.stringify({
    chart: input.chart,
    rows: input.rows.map((row) => ({
      simulationOutputId: row.simulationOutputId,
      runId: row.runId,
      metricId: row.metricId,
      value: row.value,
      occurredAt: row.occurredAt,
      createdAt: row.createdAt,
    })),
  })
}

function snapshotRequestInput(input: {
  chart: DashboardDrilldownInsightsInput["chart"]
  rows: DashboardDrilldownInsightRow[]
}): DashboardDrilldownInsightsInput {
  return {
    chart: { ...input.chart },
    rows: input.rows.map((row) => ({ ...row })),
  }
}

function reduceStatusSteps(input: {
  steps: DashboardDrilldownInsightsStatus[]
  nextStep: DashboardDrilldownInsightsStatus
}): DashboardDrilldownInsightsStatus[] {
  const existingIndex = input.steps.findIndex(
    (step) => step.stepId === input.nextStep.stepId,
  )
  if (existingIndex === -1) {
    return [...input.steps, input.nextStep]
  }

  return input.steps.map((step, index) =>
    index === existingIndex ? input.nextStep : step,
  )
}

function getAssistantText(messages: DashboardInsightsStreamMessage[]): string {
  const assistantMessage = [...messages]
    .reverse()
    .find((message) => message.role === "assistant")
  return assistantMessage?.content ?? ""
}

export function useDashboardDrilldownInsights({
  autoRun = true,
  chart,
  enabled,
  rows,
}: UseDashboardDrilldownInsightsInput): UseDashboardDrilldownInsightsResult {
  const nextMessageIdRef = useRef(0)
  const latestRequestKeyRef = useRef<string | null>(null)
  const activeRequestKeyRef = useRef<string | null>(null)
  const activeInputRef = useRef<DashboardDrilldownInsightsInput | null>(null)
  const pendingInputRef = useRef<DashboardDrilldownInsightsInput | null>(null)
  const [completedRequestKey, setCompletedRequestKey] = useState<string | null>(
    null,
  )
  const [pendingRequestKey, setPendingRequestKey] = useState<string | null>(
    null,
  )

  const requestInput = useMemo(() => {
    if (!chart || rows.length === 0) {
      return null
    }

    return snapshotRequestInput({ chart, rows })
  }, [chart, rows])

  const requestKey = useMemo(
    () => (requestInput ? buildRequestKey(requestInput) : null),
    [requestInput],
  )

  const createMessage = useCallback(
    (input: CreateMessageInput): DashboardInsightsStreamMessage => {
      nextMessageIdRef.current += 1
      return {
        id: input.id ?? `dashboard-insight-${nextMessageIdRef.current}`,
        role: input.role,
        content: input.content,
        timestamp: new Date(),
        ...(input.isStreaming !== undefined && {
          isStreaming: input.isStreaming,
        }),
      }
    },
    [],
  )

  const buildInput = useCallback((): DashboardDrilldownInsightsInput => {
    if (!activeInputRef.current) {
      throw new Error("No active dashboard drilldown insights input")
    }
    return activeInputRef.current
  }, [])

  const createUserMessage = useCallback(
    (content: string) =>
      createMessage({
        role: "user",
        content,
      }),
    [createMessage],
  )

  const createAssistantMessage = useCallback(
    (id: string) =>
      createMessage({
        id,
        role: "assistant",
        content: "",
        isStreaming: true,
      }),
    [createMessage],
  )

  const createErrorMessage = useCallback(
    (content: string) =>
      createMessage({
        role: "assistant",
        content,
      }),
    [createMessage],
  )

  const appendAssistantDelta = useCallback(
    (message: DashboardInsightsStreamMessage, delta: string) => ({
      ...message,
      content: message.content + delta,
    }),
    [],
  )

  const reduceEvent = useCallback(
    ({
      applyDefault,
      event,
      state,
    }: LlmGenerationStreamReducerArgs<
      DashboardInsightsStreamMessage,
      DashboardInsightsStreamCustomState
    >) => {
      const nextState = applyDefault(state)
      if (event.event === "status") {
        const statusStep = dashboardDrilldownInsightsStatusSchema.safeParse(
          event.data,
        )
        if (!statusStep.success) {
          return nextState
        }

        return {
          ...nextState,
          customState: {
            ...nextState.customState,
            statusSteps: reduceStatusSteps({
              steps: nextState.customState.statusSteps,
              nextStep: statusStep.data,
            }),
          },
        }
      }

      if (event.event === "output") {
        const output = dashboardDrilldownInsightsOutputSchema.safeParse(
          event.data,
        )
        if (!output.success) {
          return nextState
        }

        return {
          ...nextState,
          customState: {
            ...nextState.customState,
            output: output.data,
          },
        }
      }

      return nextState
    },
    [],
  )

  const handleComplete = useCallback(() => {
    if (latestRequestKeyRef.current === activeRequestKeyRef.current) {
      setCompletedRequestKey(activeRequestKeyRef.current)
    }
  }, [])

  const stream = useLlmGenerationStream<
    DashboardDrilldownInsightsInput,
    DashboardInsightsStreamMessage,
    DashboardInsightsStreamCustomState
  >({
    taskId: DASHBOARD_DRILLDOWN_INSIGHTS_STREAM_TASK_ID,
    initialCustomState: INITIAL_CUSTOM_STATE,
    buildInput,
    createUserMessage,
    createAssistantMessage,
    createErrorMessage,
    appendAssistantDelta,
    reduceEvent,
    onComplete: handleComplete,
  })
  const {
    customState,
    error: streamError,
    isProcessing: streamIsProcessing,
    messages: streamMessages,
    reset: resetStream,
    status: streamStatus,
    submit: submitStream,
  } = stream

  const queueRequest = useCallback(
    (nextRequestKey: string, nextInput: DashboardDrilldownInsightsInput) => {
      if (!enabled) {
        return
      }

      resetStream()
      pendingInputRef.current = nextInput
      latestRequestKeyRef.current = nextRequestKey
      setPendingRequestKey(nextRequestKey)
    },
    [enabled, resetStream],
  )

  const regenerate = useCallback(() => {
    if (!requestKey || !requestInput) {
      return
    }

    queueRequest(requestKey, requestInput)
  }, [queueRequest, requestInput, requestKey])

  useEffect(() => {
    if (!autoRun || !enabled || !requestKey || !requestInput) {
      return
    }

    if (
      completedRequestKey === requestKey ||
      activeRequestKeyRef.current === requestKey ||
      pendingRequestKey === requestKey
    ) {
      return
    }

    queueRequest(requestKey, requestInput)
  }, [
    autoRun,
    completedRequestKey,
    enabled,
    pendingRequestKey,
    queueRequest,
    requestInput,
    requestKey,
  ])

  useEffect(() => {
    if (!pendingRequestKey || streamIsProcessing) {
      return
    }

    if (
      !enabled ||
      requestKey !== pendingRequestKey ||
      pendingInputRef.current == null
    ) {
      pendingInputRef.current = null
      setPendingRequestKey(null)
      return
    }

    activeInputRef.current = pendingInputRef.current
    pendingInputRef.current = null
    activeRequestKeyRef.current = pendingRequestKey
    latestRequestKeyRef.current = pendingRequestKey
    setPendingRequestKey(null)
    void submitStream(SUBMIT_INPUT)
  }, [enabled, pendingRequestKey, requestKey, streamIsProcessing, submitStream])

  const output = customState.output
  const isCurrentOutput =
    output != null &&
    completedRequestKey != null &&
    completedRequestKey === requestKey
  const status: DashboardDrilldownInsightsStatusState =
    streamStatus === "error"
      ? "error"
      : streamIsProcessing || pendingRequestKey
        ? "loading"
        : isCurrentOutput
          ? "success"
          : "idle"
  const isStale =
    output != null &&
    completedRequestKey !== null &&
    completedRequestKey !== requestKey

  return {
    output: isCurrentOutput ? output : null,
    status,
    error: streamError ? getErrorMessage(streamError) : null,
    isLoading: status === "loading",
    isStale,
    streamedText: getAssistantText(streamMessages),
    statusSteps: customState.statusSteps,
    regenerate,
  }
}
